import { useState, useEffect } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { athletesApi } from '../../api/athletes';
import { useGroups } from '../../hooks/useGroups';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatDate, formatAge } from '../../utils/dateUtils';
import type { UpdateAthleteRequest } from '../../types/api';
import { AthleteNotesList } from '../../components/AthleteNotes/AthleteNotesList';
import { attendanceApi } from '../../api/attendance';
import { todayISO } from '../../utils/attendance';
import { liveGroupId } from '../../utils/liveGroup';
import { AttendanceHistory } from './AttendanceHistory';
import { AddAthleteNoteSheet } from '../../components/AthleteNotes/AddAthleteNoteSheet';

export function AthleteDetailScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const athleteId = currentScreen.params.athleteId as string;

  // Историю посещений тянем только когда вкладку открыли: на карточке
  // спортсмена она нужна не всегда, а запрос не бесплатный.
  const attendanceFrom = todayISO(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
  const attendanceTo = todayISO();

  const [isEditing, setIsEditing] = useState(false);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showSportData, setShowSportData] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'attendance' | 'notes'>('general');
  const [showAddNoteSheet, setShowAddNoteSheet] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    parent_phone: '',
    parent_email: '',
    email: '',
    address: '',
    notes: '',
    date_of_birth: '',
    group_id: '',
    weight: '' as string,
    height: '' as string,
    medical_clearance: '',
    sport_data: {} as Record<string, string>,
  });
  const [newSportField, setNewSportField] = useState({ key: '', value: '' });

  const { data: athlete, isLoading } = useQuery({
    queryKey: ['athlete', athleteId],
    queryFn: () => athletesApi.getById(athleteId),
    enabled: !!athleteId,
  });

  const { data: groups } = useGroups(currentAssistantId || '');

  // Хуки должны вызываться до ранних return ниже: иначе после загрузки
  // спортсмена их число меняется, React падает и экран остаётся пустым.
  const { data: attendanceItems, isLoading: isLoadingAttendance } = useQuery({
    queryKey: ['athlete-attendance', athleteId, attendanceFrom, attendanceTo],
    queryFn: () => attendanceApi.getByAthleteAndDateRange(athleteId, attendanceFrom, attendanceTo),
    enabled: !!athleteId && activeTab === 'attendance',
  });

  const { data: attendanceStats } = useQuery({
    queryKey: ['athlete-attendance-stats', athleteId, attendanceFrom, attendanceTo],
    queryFn: () => attendanceApi.getStats(athleteId, attendanceFrom, attendanceTo),
    enabled: !!athleteId && activeTab === 'attendance',
  });

  useEffect(() => {
    if (athlete) {
      setFormData({
        name: athlete.name || '',
        phone: athlete.phone || '',
        parent_phone: athlete.parent_phone || '',
        parent_email: athlete.parent_email || '',
        email: athlete.email || '',
        address: athlete.address || '',
        notes: athlete.notes || '',
        date_of_birth: athlete.date_of_birth || '',
        group_id: athlete.group_id || '',
        weight: athlete.weight != null ? String(athlete.weight) : '',
        height: athlete.height != null ? String(athlete.height) : '',
        medical_clearance: athlete.medical_clearance || '',
        sport_data: (athlete.sport_data as Record<string, string>) || {},
      });
    }
  }, [athlete]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAthleteRequest) => athletesApi.update(athleteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete', athleteId] });
      queryClient.invalidateQueries({ queryKey: ['athletes'] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      console.error('Failed to update athlete:', error);
      alert('Не удалось обновить данные спортсмена');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => athletesApi.delete(athleteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athletes'] });
      popScreen();
    },
    onError: (error: Error) => {
      console.error('Failed to delete athlete:', error);
      alert('Не удалось удалить спортсмена');
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      name: formData.name,
      phone: formData.phone,
      parent_phone: formData.parent_phone,
      parent_email: formData.parent_email,
      email: formData.email,
      address: formData.address,
      notes: formData.notes,
      date_of_birth: formData.date_of_birth || undefined,
      group_id: formData.group_id || undefined,
      weight: formData.weight ? parseFloat(formData.weight) : undefined,
      height: formData.height ? parseInt(formData.height, 10) : undefined,
      medical_clearance: formData.medical_clearance || undefined,
      sport_data: Object.keys(formData.sport_data).length > 0 ? formData.sport_data : undefined,
    });
  };

  const handleAddSportField = () => {
    if (newSportField.key.trim() && newSportField.value.trim()) {
      setFormData({
        ...formData,
        sport_data: {
          ...formData.sport_data,
          [newSportField.key.trim()]: newSportField.value.trim(),
        },
      });
      setNewSportField({ key: '', value: '' });
    }
  };

  const handleRemoveSportField = (key: string) => {
    const newSportData = { ...formData.sport_data };
    delete newSportData[key];
    setFormData({ ...formData, sport_data: newSportData });
  };

  const handleUpdateSportField = (key: string, value: string) => {
    setFormData({
      ...formData,
      sport_data: { ...formData.sport_data, [key]: value },
    });
  };

  const handleDelete = () => {
    if (window.confirm('Вы уверены, что хотите удалить спортсмена?')) {
      deleteMutation.mutate();
    }
  };

  const handleCancel = () => {
    if (athlete) {
      setFormData({
        name: athlete.name || '',
        phone: athlete.phone || '',
        parent_phone: athlete.parent_phone || '',
        parent_email: athlete.parent_email || '',
        email: athlete.email || '',
        address: athlete.address || '',
        notes: athlete.notes || '',
        date_of_birth: athlete.date_of_birth || '',
        group_id: athlete.group_id || '',
        weight: athlete.weight != null ? String(athlete.weight) : '',
        height: athlete.height != null ? String(athlete.height) : '',
        medical_clearance: athlete.medical_clearance || '',
        sport_data: (athlete.sport_data as Record<string, string>) || {},
      });
    }
    setNewSportField({ key: '', value: '' });
    setIsEditing(false);
  };

  const handleBack = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      popScreen();
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-hint)',
    marginBottom: '4px',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="p-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>
            Спортсмен не найден
          </p>
        </Card>
      </div>
    );
  }

  const selectedGroup = groups?.find((g) => g.id === formData.group_id);

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title={isEditing ? 'Редактирование' : athlete.name}
        onBackClick={handleBack}
        rightAction={
          !isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="text-[15px] font-medium"
              style={{ color: 'var(--accent)' }}
            >
              Редактировать
            </button>
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 bg-[var(--card-bg)] rounded-lg p-1">
        <button
          onClick={() => setActiveTab('general')}
          className="flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'general' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'general' ? '#FFFFFF' : 'var(--text-primary)',
          }}
        >
          Общее
        </button>
        <button
          onClick={() => setActiveTab('attendance')}
          className="flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'attendance' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'attendance' ? '#FFFFFF' : 'var(--text-primary)',
          }}
        >
          Посещения
        </button>
        <button
          onClick={() => setActiveTab('notes')}
          className="flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors"
          style={{
            backgroundColor: activeTab === 'notes' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'notes' ? '#FFFFFF' : 'var(--text-primary)',
          }}
        >
          Записи
        </button>
      </div>

      {/* Attendance tab content */}
      {activeTab === 'attendance' && (
        <Card>
          <AttendanceHistory
            items={attendanceItems ?? []}
            stats={attendanceStats}
            isLoading={isLoadingAttendance}
            groupNameById={(groupId) => groups?.find((g) => g.id === groupId)?.name}
          />
        </Card>
      )}

      {/* General tab content */}
      {activeTab === 'general' && (
      <Card>
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label style={labelStyle}>ФИО *</label>
            {isEditing ? (
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Например: Иванов Иван Иванович"
                style={inputStyle}
                disabled={updateMutation.isPending}
              />
            ) : (
              <p style={{ color: 'var(--text-primary)' }}>{athlete.name}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label style={labelStyle}>Телефон спортсмена</label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+7 (999) 123-45-67"
                style={inputStyle}
                disabled={updateMutation.isPending}
              />
            ) : (
              <p style={{ color: 'var(--text-primary)' }}>{athlete.phone || '--'}</p>
            )}
          </div>

          {/* Parent phone */}
          <div>
            <label style={labelStyle}>Телефон родителя</label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.parent_phone}
                onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                placeholder="+7 (999) 123-45-67"
                style={inputStyle}
                disabled={updateMutation.isPending}
              />
            ) : (
              <p style={{ color: 'var(--text-primary)' }}>{athlete.parent_phone || '--'}</p>
            )}
          </div>

          {/* Group */}
          <div>
            <label style={labelStyle}>Группа</label>
            {isEditing ? (
              <select
                // Удалённой группы в списке нет - показываем «Без группы».
                // Сохранение без выбора группы ссылку не трогает.
                value={liveGroupId(formData.group_id, groups) ?? ''}
                onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
                disabled={updateMutation.isPending}
              >
                <option value="">Без группы</option>
                {groups?.map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            ) : (
              <p style={{ color: 'var(--text-primary)' }}>{selectedGroup?.name || '--'}</p>
            )}
          </div>

          {/* Weight & Height */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label style={labelStyle}>Вес (кг)</label>
              {isEditing ? (
                <input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  placeholder="59"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
              ) : (
                <p style={{ color: 'var(--text-primary)' }}>
                  {athlete.weight != null ? `${athlete.weight} кг` : '--'}
                </p>
              )}
            </div>
            <div className="flex-1">
              <label style={labelStyle}>Рост (см)</label>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.height}
                  onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  placeholder="165"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
              ) : (
                <p style={{ color: 'var(--text-primary)' }}>
                  {athlete.height != null ? `${athlete.height} см` : '--'}
                </p>
              )}
            </div>
          </div>

          {/* Additional fields accordion */}
          <div style={{ borderTop: '1px solid var(--separator)', paddingTop: '16px' }}>
            <button
              type="button"
              onClick={() => setShowAdditionalFields(!showAdditionalFields)}
              className="flex items-center justify-between w-full"
              disabled={updateMutation.isPending}
            >
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {showAdditionalFields ? '\u25BC' : '\u25B6'} Дополнительные поля
              </span>
              <span className="text-xs" style={{ color: 'var(--text-hint)' }}>
                {showAdditionalFields ? 'Скрыть' : 'Показать'}
              </span>
            </button>

            {showAdditionalFields && (
              <div className="mt-4 space-y-4">
                <div>
                  <label style={labelStyle}>Дата рождения</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      style={inputStyle}
                      disabled={updateMutation.isPending}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <p style={{ color: 'var(--text-primary)' }}>{formatDate(athlete.date_of_birth) || '--'}</p>
                      {athlete.date_of_birth && (
                        <span className="text-sm" style={{ color: 'var(--text-hint)' }}>
                          ({formatAge(athlete.date_of_birth)})
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Email спортсмена</label>
                  {isEditing ? (
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="athlete@example.com" style={inputStyle} disabled={updateMutation.isPending} />
                  ) : (
                    <p style={{ color: 'var(--text-primary)' }}>{athlete.email || '--'}</p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Email родителя</label>
                  {isEditing ? (
                    <input type="email" value={formData.parent_email} onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })} placeholder="parent@example.com" style={inputStyle} disabled={updateMutation.isPending} />
                  ) : (
                    <p style={{ color: 'var(--text-primary)' }}>{athlete.parent_email || '--'}</p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Адрес</label>
                  {isEditing ? (
                    <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="г. Москва, ул. Ленина, д. 1" style={inputStyle} disabled={updateMutation.isPending} />
                  ) : (
                    <p style={{ color: 'var(--text-primary)' }}>{athlete.address || '--'}</p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Заметки</label>
                  {isEditing ? (
                    <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Дополнительная информация о спортсмене" rows={3} style={{ ...inputStyle, resize: 'none' }} disabled={updateMutation.isPending} />
                  ) : (
                    <p className="whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{athlete.notes || '--'}</p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Мед. допуск (до)</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={formData.medical_clearance}
                      onChange={(e) => setFormData({ ...formData, medical_clearance: e.target.value })}
                      style={inputStyle}
                      disabled={updateMutation.isPending}
                    />
                  ) : (
                    <p style={{ color: 'var(--text-primary)' }}>{formatDate(athlete.medical_clearance) || '--'}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sport data */}
          {(Object.keys(athlete?.sport_data || {}).length > 0 || isEditing) && (
            <div style={{ borderTop: '1px solid var(--separator)', paddingTop: '16px' }}>
              <button
                type="button"
                onClick={() => setShowSportData(!showSportData)}
                className="flex items-center justify-between w-full"
                disabled={updateMutation.isPending}
              >
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {showSportData ? '\u25BC' : '\u25B6'} Спортивные данные
                </span>
                <span className="text-xs" style={{ color: 'var(--text-hint)' }}>
                  {showSportData ? 'Скрыть' : 'Показать'}
                </span>
              </button>

              {showSportData && (
                <div className="mt-4 space-y-3">
                  {Object.entries(formData.sport_data).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2">
                      <div className="flex-1">
                        <label className="text-xs" style={{ color: 'var(--text-hint)' }}>{key}</label>
                        {isEditing ? (
                          <input type="text" value={value} onChange={(e) => handleUpdateSportField(key, e.target.value)} style={inputStyle} disabled={updateMutation.isPending} />
                        ) : (
                          <p className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>{value}</p>
                        )}
                      </div>
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveSportField(key)}
                          disabled={updateMutation.isPending}
                          className="mt-6 p-2 rounded transition-colors"
                          style={{ color: 'var(--danger)' }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}

                  {isEditing && (
                    <div style={{ borderTop: '1px solid var(--separator)', paddingTop: '8px' }}>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-hint)' }}>Добавить новое поле</p>
                      <div className="flex gap-2">
                        <input type="text" value={newSportField.key} onChange={(e) => setNewSportField({ ...newSportField, key: e.target.value })} placeholder="Название" style={{ ...inputStyle, flex: 1 }} disabled={updateMutation.isPending} />
                        <input type="text" value={newSportField.value} onChange={(e) => setNewSportField({ ...newSportField, value: e.target.value })} placeholder="Значение" style={{ ...inputStyle, flex: 1 }} disabled={updateMutation.isPending} />
                        <Button onClick={handleAddSportField} disabled={updateMutation.isPending || !newSportField.key || !newSportField.value}>
                          +
                        </Button>
                      </div>
                    </div>
                  )}

                  {!isEditing && Object.keys(formData.sport_data).length === 0 && (
                    <p className="text-sm italic" style={{ color: 'var(--text-hint)' }}>Нет данных</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {isEditing && (
            <div className="flex gap-2 pt-4" style={{ borderTop: '1px solid var(--separator)' }}>
              <Button onClick={handleSave} disabled={updateMutation.isPending || !formData.name} fullWidth>
                {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button onClick={handleCancel} disabled={updateMutation.isPending} variant="secondary">
                Отмена
              </Button>
            </div>
          )}

          {!isEditing && (
            <div className="space-y-2 pt-4" style={{ borderTop: '1px solid var(--separator)' }}>
              <Button onClick={handleDelete} disabled={deleteMutation.isPending} variant="danger" fullWidth>
                {deleteMutation.isPending ? 'Удаление...' : 'Удалить спортсмена'}
              </Button>
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Notes tab content */}
      {activeTab === 'notes' && (
        <div className="space-y-4">
          <AthleteNotesList
            athleteId={athleteId}
            onAddNote={() => setShowAddNoteSheet(true)}
          />
        </div>
      )}

      {/* Add Note Sheet */}
      {showAddNoteSheet && (
        <AddAthleteNoteSheet
          athleteId={athleteId}
          onClose={() => setShowAddNoteSheet(false)}
        />
      )}
    </div>
  );
}
